import {
	CognitoAccessToken,
	CognitoIdToken,
	CognitoRefreshToken,
	CognitoUser,
	CognitoUserPool as CognitoUserPoolConstructor,
	CognitoUserSession,
} from 'amazon-cognito-identity-js';
import { Amplify } from 'aws-amplify';
import {
	signOut as amplifySignOut,
	fetchAuthSession as fetchAuthSessionFn,
	getCurrentUser as getCurrentUserFn,
} from 'aws-amplify/auth';
import { cognitoUserPoolsTokenProvider as cognitoUserPoolsTokenProviderFn } from 'aws-amplify/auth/cognito';
import 'aws-amplify/auth/enable-oauth-listener';
import jwt, { JwtPayload } from 'jsonwebtoken';

import { Cookie, HandleSignOutArgs } from '../types';
import { removeAuthCookies } from './CookieCleanup';

interface CustomJwtPayload extends JwtPayload {
	'cognito:username'?: string;
}

export const cognitoUserPoolsTokenProvider: typeof cognitoUserPoolsTokenProviderFn = cognitoUserPoolsTokenProviderFn;
export const getCurrentUser = getCurrentUserFn;
export const fetchAuthSession = fetchAuthSessionFn;
export const CognitoUserPool = CognitoUserPoolConstructor;
export type CognitoUserPoolType = CognitoUserPoolConstructor;
export const configureAmplify = (config: any, ssr: boolean) => {
	Amplify.configure(config, { ssr });
};

const requiredCookies = ['aws_cognito_id_token', 'aws_cognito_access_token', 'aws_cognito_refresh_token'];

const cognitoCookieNames = [
	...requiredCookies,
	'aws_cognito_email_address',
	'aws_cognito_expires_at',
	'aws_cognito_expires_in',
	'aws_cognito_last_auth_user',
	'aws_cognito_token_type',
];

const validateCookies = (cookies: Cookie): boolean => requiredCookies.every((cookie) => cookie in cookies);

export const decodeJwtToken = (token?: string): JwtPayload | null => {
	if (!token) return null;

	try {
		return jwt.decode(token) as JwtPayload;
	} catch (error) {
		console.error('Failed to decode JWT token:', error);
		return null;
	}
};

function loginWithCookies(cookies: Cookie, userPool: CognitoUserPoolType, currentUser: CognitoUser | null) {
	if (!validateCookies(cookies)) {
		console.warn('Required cookies are missing');
		return;
	}
	const decodedIdToken = jwt.decode(cookies.aws_cognito_id_token) as JwtPayload;
	if (decodedIdToken?.exp) {
		const currentTimeInSeconds = Math.floor(Date.now() / 1000);
		if (currentTimeInSeconds < decodedIdToken.exp) {
			const IdToken = new CognitoIdToken({ IdToken: cookies.aws_cognito_id_token });
			const AccessToken = new CognitoAccessToken({ AccessToken: cookies.aws_cognito_access_token });
			const RefreshToken = new CognitoRefreshToken({ RefreshToken: cookies.aws_cognito_refresh_token });
			const Username = IdToken.payload.sub;
			const user = currentUser || new CognitoUser({ Pool: userPool, Username });
			user.setSignInUserSession(
				new CognitoUserSession({
					AccessToken,
					IdToken,
					RefreshToken,
				}),
			);
		}
	}
}

export const handleSignIn = async (
	cookies: Cookie,
	userPoolId: string,
	clientId: string,
	throwErr: boolean = false,
) => {
	const userPool = new CognitoUserPool({ UserPoolId: userPoolId, ClientId: clientId });
	if (!validateCookies(cookies)) {
		console.warn('Required cookies are missing');
		if (throwErr) {
			throw new Error();
		}

		return;
	}
	try {
		const currentUser = userPool.getCurrentUser();
		if (!currentUser) {
			loginWithCookies(cookies, userPool, null);
			return;
		}
		const { tokens } = (await fetchAuthSession()) ?? {};
		if (!tokens) {
			loginWithCookies(cookies, userPool, currentUser);
		} else {
			const sessionUserSub = tokens.accessToken?.payload?.sub;
			const cookieUserSub = jwt.decode(cookies.aws_cognito_id_token)?.sub;
			if (sessionUserSub && cookieUserSub && sessionUserSub !== cookieUserSub) {
				currentUser.signOut();
				loginWithCookies(cookies, userPool, currentUser);
			}
		}
	} catch (error) {
		console.error('Login failed:', error);
		if (throwErr) {
			throw error;
		}
	}
};

export const SSOLogin = async (
	code: string,
	userPoolId: string,
	clientId: string,
	setCookies: any,
	cognitoDomain: string,
	redirectUri: string,
) => {
	try {
		const userPool = new CognitoUserPool({ UserPoolId: userPoolId, ClientId: clientId });
		const result = await fetch(`${cognitoDomain}/oauth2/token`, {
			method: 'POST',
			headers: {
				'Content-Type': 'application/x-www-form-urlencoded',
			},
			body: new URLSearchParams({
				grant_type: 'authorization_code',
				code,
				client_id: clientId,
				redirect_uri: redirectUri,
			}),
		});
		if (!result?.ok) return;
		const authResult = await result.json();
		if (authResult?.error) return;
		const IdToken = new CognitoIdToken({ IdToken: authResult.id_token });
		const AccessToken = new CognitoAccessToken({ AccessToken: authResult.access_token });
		const RefreshToken = new CognitoRefreshToken({ RefreshToken: authResult.refresh_token });
		const decodedToken = jwt.decode(authResult.id_token) as CustomJwtPayload;
		if (!decodedToken) return;
		const Username = decodedToken['cognito:username'];
		if (!Username) return;
		const user = new CognitoUser({ Pool: userPool, Username });
		user.setSignInUserSession(
			new CognitoUserSession({
				AccessToken,
				IdToken,
				RefreshToken,
			}),
		);
		setCookies(authResult, window.location.hostname, window.location.protocol);
	} catch (error) {
		console.error('SSO Login Failed', error);
	}
};

export const handleSignOut = async ({
	cookies,
	removeCookie,
	UserPoolId,
	ClientId,
	URL,
	cookieDomain,
	client,
}: HandleSignOutArgs) => {
	const awsCookieNames = new Set([
		...cognitoCookieNames,
		...Object.keys(cookies).filter((cookieName) => cookieName.startsWith('aws_cognito')),
	]);
	const hostname = window.location.hostname;
	const inferredDomain = hostname.includes('localhost') ? undefined : hostname.split('.').slice(-2).join('.');

	try {
		// Revoke the Cognito session before deleting its tokens so other
		// ParkChirp applications cannot continue using a host-scoped cookie.
		await amplifySignOut({ global: true });
	} catch (err) {
		console.error('Error revoking Cognito session!', err);
	}

	try {
		if (client) {
			await client.clearStore();
			await client.cache.reset();
		}
	} catch (err) {
		console.error('Error clearing client state during sign out!', err);
	} finally {
		try {
			const currentUser = new CognitoUserPool({ UserPoolId, ClientId }).getCurrentUser();
			currentUser?.signOut();
		} catch (err) {
			console.error('Error clearing legacy Cognito state during sign out!', err);
		}

		removeAuthCookies(awsCookieNames, removeCookie, [cookieDomain, inferredDomain]);

		localStorage.clear();
		sessionStorage.clear();
		window.location.href = URL;
	}
};
